import { create } from "zustand"
import { PRINT_COMMAND_TIMEOUT_MILLISECONDS } from "@constants"

// Define the shape of our printer object
type EpsonPrinter = epson.Printer

type ConnectionStatus = "DISCONNECTED" | "CONNECTING" | "CONNECTED"
type PaperStatus = "OK" | "LOW" | "END"
type ErrorStatus = "NONE" | "MECHANICAL" | "AUTOCUTTER" | "UNRECOVERABLE" | "AUTORECOVERABLE" | "UNKNOWN"

export interface PrinterStatusObject {
  isOnline: boolean
  isCoverOpen: boolean
  paperStatus: PaperStatus
  isDrawerOpen: boolean
  isPaperFeeding: boolean
  isPanelSwitchPressed: boolean
  isBuzzerSounding: boolean
  isWaitingForPaperRemoval: boolean
  printerError: ErrorStatus
}

export interface PrinterStates {
  connection: ConnectionStatus
  printer: EpsonPrinter | null
  status: PrinterStatusObject
}

export interface PrinterActions {
  setPrinter: (printer: EpsonPrinter | null) => void
  setConnection: (status: ConnectionStatus) => void
  updatePrinterStatus: (updates: Partial<PrinterStatusObject>) => void
  reset: () => void
  executeCommand: (commandFn?: (printer: EpsonPrinter) => void) => Promise<epson.PrinterReceiveEvent>
  updateStatusFromASB: (status: number) => void
}

export type PrinterStore = PrinterStates & PrinterActions

const initialPrinterStatus: PrinterStatusObject = {
  isOnline: false,
  isCoverOpen: false,
  paperStatus: "OK",
  isDrawerOpen: false,
  isPaperFeeding: false,
  isPanelSwitchPressed: false,
  isBuzzerSounding: false,
  isWaitingForPaperRemoval: false,
  printerError: "NONE",
}

const initialState: PrinterStates = {
  connection: "DISCONNECTED",
  printer: null,
  status: initialPrinterStatus,
}

export const usePrinterStore = create<PrinterStore>((set, get) => ({
  ...initialState,

  setPrinter: (printer) => set({ printer }),
  setConnection: (status) => set({ connection: status }),
  updatePrinterStatus: (updates) =>
    set((state) => ({
      status: { ...state.status, ...updates },
    })),
  reset: () => set(initialState),

  updateStatusFromASB: (status: number) => {
    const printerClass = get().printer

    if (!printerClass) return

    const ASB_WAIT_REMOVE_LABEL = 0x10000000

    let isOnline = (status & printerClass.ASB_OFF_LINE) === 0
    let isCoverOpen = (status & printerClass.ASB_COVER_OPEN) !== 0
    let isPaperEnd = (status & printerClass.ASB_RECEIPT_END) !== 0

    let paperStatus: "OK" | "LOW" | "END" = "OK"

    if (isPaperEnd) {
      paperStatus = "END"
    } else if ((status & printerClass.ASB_RECEIPT_NEAR_END) !== 0) {
      paperStatus = "LOW"
    }

    // Epson SDK drawer logic depends on drawerOpenLevel:
    // - DRAWER_OPEN_LEVEL_HIGH (1): ASB_DRAWER_KICK bit set = drawer open
    // - DRAWER_OPEN_LEVEL_LOW (0, default): ASB_DRAWER_KICK bit NOT set = drawer open
    const isDrawerKickBitSet = (status & printerClass.ASB_DRAWER_KICK) !== 0
    let isDrawerOpen =
      printerClass.drawerOpenLevel === printerClass.DRAWER_OPEN_LEVEL_HIGH ? isDrawerKickBitSet : !isDrawerKickBitSet
    let isPaperFeeding = (status & printerClass.ASB_PAPER_FEED) !== 0
    let isPanelSwitchPressed = (status & printerClass.ASB_PANEL_SWITCH) !== 0
    let isBuzzerSounding = (status & printerClass.ASB_BUZZER) !== 0
    let isWaitingForPaperRemoval = (status & ASB_WAIT_REMOVE_LABEL) !== 0

    let printerError: ErrorStatus = "NONE"

    if ((status & printerClass.ASB_UNRECOVER_ERR) !== 0) {
      printerError = "UNRECOVERABLE"
    } else if ((status & printerClass.ASB_AUTORECOVER_ERR) !== 0) {
      printerError = "AUTORECOVERABLE"
    } else if ((status & printerClass.ASB_MECHANICAL_ERR) !== 0) {
      printerError = "MECHANICAL"
    } else if ((status & printerClass.ASB_AUTOCUTTER_ERR) !== 0) {
      printerError = "AUTOCUTTER"
    }

    set((state) => ({
      status: {
        ...state.status,
        isOnline,
        isCoverOpen,
        paperStatus,
        isDrawerOpen,
        isPaperFeeding,
        isPanelSwitchPressed,
        isBuzzerSounding,
        isWaitingForPaperRemoval,
        printerError,
      },
    }))
  },

  executeCommand: (commandFn) => {
    const { printer, updateStatusFromASB } = get()

    return new Promise((resolve, reject) => {
      if (!printer) {
        reject(new Error("Printer is null"))

        return
      }

      try {
        if (commandFn) {
          commandFn(printer)
        } else {
          printer.addCommand("") // Send empty command for status update if no commandFn
        }

        printer.send()

        const timeout = setTimeout(() => {
          reject(new Error("Command timed out"))
        }, PRINT_COMMAND_TIMEOUT_MILLISECONDS)

        printer.onreceive = (res) => {
          updateStatusFromASB(res.status)
          clearTimeout(timeout)
          resolve(res)
        }
      } catch (err) {
        reject(err)
      }
    })
  },
}))
